import { FC } from "react";
import { Helmet } from "react-helmet-async";

type HelmetData = {
  pageTitle: string;
  pageDescription: string;
  pageUrl: string;
  pageKeywords?: string;
  ogImage?: string;
  twitterImage?: string;
  preloadImage?: string;
  structuredData?: object | object[];
  noindex?: boolean;
  nofollow?: boolean;
  articleAuthor?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleSection?: string;
  articleTags?: string[];
  hreflang?: Array<{ lang: string; url: string }>;
  alternateLanguages?: Array<{ lang: string; url: string }>;
};

const SeoHelmet: FC<HelmetData> = ({
  pageTitle,
  pageDescription,
  pageUrl,
  pageKeywords,
  ogImage,
  twitterImage,
  preloadImage,
  structuredData,
  noindex = false,
  nofollow = false,
  articleAuthor,
  articlePublishedTime,
  articleModifiedTime,
  articleSection,
  articleTags,
  hreflang,
  alternateLanguages,
}) => {
  const siteName = process.env.REACT_APP_SITE_NAME || "BIGELEMENT INFOTECH";
  const siteUrl = process.env.REACT_APP_SITE_URL || "";
  const pageFullUrl = `${siteUrl}${pageUrl}`;
  const defaultOgImage = ogImage || `${siteUrl}/assets/images/beit_logo.svg`;
  const defaultTwitterImage = twitterImage || defaultOgImage;
  
  // Default keywords if not provided
  const defaultKeywords = pageKeywords || 
    "web development, product development, UI/UX design, software development, India, digital transformation";
  
  // Robots meta
  const robotsContent = `${noindex ? "noindex" : "index"}, ${nofollow ? "nofollow" : "follow"}`;

  return (
    <Helmet prioritizeSeoTags>
      {preloadImage && (
        <link rel="preload" href={preloadImage} as="image" />
      )}
      <title>
        {siteName} - {pageTitle}
      </title>
      <meta name="title" content={`${siteName} - ${pageTitle}`} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={defaultKeywords} />
      <meta name="author" content={siteName} />
      <meta name="robots" content={robotsContent} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="copyright" content={siteName} />

      {/* Canonical URL */}
      <link rel="canonical" href={pageFullUrl} />

      {/* Hreflang Tags for International SEO */}
      {hreflang && hreflang.length > 0 && (
        <>
          {hreflang.map((item, index) => (
            <link key={index} rel="alternate" hrefLang={item.lang} href={item.url} />
          ))}
        </>
      )}
      {alternateLanguages && alternateLanguages.length > 0 && (
        <>
          {alternateLanguages.map((item, index) => (
            <link key={index} rel="alternate" hrefLang={item.lang} href={item.url} />
          ))}
        </>
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={pageFullUrl} />
      <meta property="og:title" content={`${siteName} - ${pageTitle}`} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={defaultOgImage} />
      {defaultOgImage.startsWith("https") && (
        <meta property="og:image:secure_url" content={defaultOgImage} />
      )}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Article-specific Open Graph tags */}
      {articleAuthor && <meta property="article:author" content={articleAuthor} />}
      {articlePublishedTime && <meta property="article:published_time" content={articlePublishedTime} />}
      {articleModifiedTime && <meta property="article:modified_time" content={articleModifiedTime} />}
      {articleSection && <meta property="article:section" content={articleSection} />}
      {articleTags && articleTags.length > 0 && (
        <>
          {articleTags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageFullUrl} />
      <meta name="twitter:title" content={`${siteName} - ${pageTitle}`} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={defaultTwitterImage} />
      <meta name="twitter:image:alt" content={pageTitle} />
      <meta name="twitter:creator" content="@bigelement" />
      <meta name="twitter:site" content="@bigelement" />

      {/* Additional SEO Meta Tags */}
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <meta name="theme-color" content="#000000" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      <meta name="apple-mobile-web-app-title" content={siteName} />
      
      {/* Additional SEO enhancements */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta httpEquiv="x-ua-compatible" content="IE=edge" />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((data, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )
      )}
    </Helmet>
  );
};

export default SeoHelmet;
